import React, { Fragment, useEffect, useRef, useState } from "react";
import debounce from 'lodash/debounce';
import { Button, FormGroup, Label } from 'reactstrap';
import Select, { components } from "react-select";
import { codeWithColourAutoSuggestion } from "../../../../../services/manage-product/productService";

const AvailableIn = (props) => {
    const { setValue, trigger, settingAvailableInTypes, availableInRows, setAvailableInRows } = props;

    // New: duplicate tracking state for 'type' only
    const [duplicateIndices, setDuplicateIndices] = useState([]);

    const [availableInOptions, setAvailableInOptions] = useState([]);
    const [pageAvailableIn, setPageAvailableIn] = useState(1);
    const [hasMoreAvailableIn, setHasMoreAvailableIn] = useState(true);
    const [loadingAvailableIn, setLoadingAvailableIn] = useState(false);
    const [queryAvailableIn, setQueryAvailableIn] = useState('');

    // Fetch functions (copy from general.jsx, but use your API import)
    const fetchAvailableInOptions = async (inputValue, page, currentRowIdx = null) => {
        if (loadingAvailableIn || !hasMoreAvailableIn) return;
        setLoadingAvailableIn(true);
        try {
            // Build exclude_ids from other rows' selected items (ins), exclude current row
            const excludeIds = [];
            (availableInRows || []).forEach((row, i) => {
                if (i !== currentRowIdx) return;
                (row.ins || []).forEach(item => {
                    if (!item) return;
                    const id = typeof item === 'object' ? item.id : item;
                    if (id) excludeIds.push(id);
                });
            });

            let searchData = { 
                q: inputValue, 
                page: page, 
                page_size: 10,
            };
            if (excludeIds.length) searchData.exclude_ids = Array.from(new Set(excludeIds));
            const response = await codeWithColourAutoSuggestion(searchData);
            const newOptions = response.response;
            setAvailableInOptions(prevOptions => (page === 1 ? newOptions : [...prevOptions, ...newOptions]));
            setHasMoreAvailableIn(newOptions.length === 10);
            setPageAvailableIn(page + 1);
            setLoadingAvailableIn(false);
        } catch (error) {
            setLoadingAvailableIn(false);
            console.error('Error fetching availableIn options:', error);
        }
    };

    // Debounced fetch
    const debouncedFetchAvailableInOptions = React.useCallback(debounce((inputValue, page, currentRowIdx) => fetchAvailableInOptions(inputValue, page, currentRowIdx), 500), [availableInRows]);

    // Infinite scroll handlers
    const handleMenuScrollToBottomAvailableIn = (idx) => {
        if (hasMoreAvailableIn) {
            fetchAvailableInOptions(queryAvailableIn, pageAvailableIn, idx);
        }
    };

    // Menu open handlers
    const handleMenuOpenAvailableIn = (idx) => {
        fetchAvailableInOptions(queryAvailableIn, 1, idx);
    };

    // Input change handlers
    const handleInputChangeAvailableIn = (inputValue, idx) => {
        setPageAvailableIn(1);
        setHasMoreAvailableIn(true);
        setQueryAvailableIn(inputValue);
        debouncedFetchAvailableInOptions(inputValue, 1, idx);
    };

    const handleAvailableLengthRowChange = (idx, field, value) => {
        // If type changes, reset dependent 'ins' so stale selections don't persist
        if (field === 'type') {
            setAvailableInRows(prev => prev.map((row, i) => i === idx ? { ...row, type: value, ins: [] } : row));
        } else {
            setAvailableInRows(prev => prev.map((row, i) => i === idx ? { ...row, [field]: value } : row));
        }
    };

    const handleAddAvailableLengthRow = () => {
        setAvailableInRows(prev => ([...prev, { type: '', ins: [] }]));
    };

    const handleRemoveAvailableLengthRow = (idx) => {
        setAvailableInRows(prev => prev.length > 0 ? prev.filter((_, i) => i !== idx) : prev);
    };

    useEffect(() => {
        setValue("available_ins", [...availableInRows]);
        trigger("available_ins");
    }, [availableInRows]);

    // New: detect duplicate 'type' values and expose a form-level flag
    useEffect(() => {
        const keyMap = {};
        (availableInRows || []).forEach((row, idx) => {
            const key = `${row.type || ''}`;
            const isEmpty = !row.type;
            if (isEmpty) return; // ignore empty rows
            if (!keyMap[key]) keyMap[key] = [];
            keyMap[key].push(idx);
        });
        const duplicates = new Set();
        Object.values(keyMap).forEach(arr => { if (arr.length > 1) arr.forEach(i => duplicates.add(i)); });
        const dupArray = Array.from(duplicates);
        setDuplicateIndices(dupArray);
        const hasDuplicates = dupArray.length > 0;
        if (setValue) setValue("available_ins_has_duplicates", hasDuplicates);
        if (trigger) trigger("available_ins_has_duplicates");
    }, [availableInRows, setValue, trigger]);

    return (
        <FormGroup>
            <Label className="form-label ai-form-heading">Also Available In (Mature, Modesty..)</Label>
            <table className="table table-bordered table-sm align-middle">
                <thead>
                    <tr>
                        <th style={{ width: '30%' }}>Type</th>
                        <th style={{ width: '60%' }}>Items</th>
                        <th style={{ width: '10%' }}>Action</th>
                    </tr>
                </thead>
                <tbody>
                    {(availableInRows || []).map((row, idx) => {
                        const isDuplicate = duplicateIndices.includes(idx);

                        const selectErrorStyles = {
                            control: (base, state) => ({
                                ...base,
                                borderColor: isDuplicate ? '#dc3545' : base.borderColor,
                                boxShadow: isDuplicate ? '0 0 0 1px rgba(220,53,69,0.12)' : base.boxShadow,
                                '&:hover': { borderColor: isDuplicate ? '#dc3545' : base.borderColor },
                            })
                        };

                        return (
                            <tr key={idx}>
                                <td>
                                    <Select
                                        placeholder="Select Available In Type"
                                        options={settingAvailableInTypes || []}
                                        getOptionValue={option => option.id}
                                        getOptionLabel={option => option.name}
                                        value={settingAvailableInTypes?.find(opt => opt.id === row.type) || null}
                                        onChange={selected => handleAvailableLengthRowChange(idx, 'type', selected ? selected.id : '')}
                                        isClearable={true}
                                        styles={selectErrorStyles}
                                    />
                                </td>
                                <td>
                                    <Select
                                        placeholder="Select Available In Items"
                                        isMulti
                                        options={availableInOptions}
                                        getOptionValue={option => option.id}
                                        getOptionLabel={option => option.name}
                                        value={row.ins}
                                        onChange={selected => handleAvailableLengthRowChange(idx, 'ins', selected)}
                                        onInputChange={value => handleInputChangeAvailableIn(value, idx)}
                                        onMenuScrollToBottom={() => handleMenuScrollToBottomAvailableIn(idx)}
                                        onMenuOpen={() => handleMenuOpenAvailableIn(idx)}
                                        isLoading={loadingAvailableIn}
                                        isClearable={true}
                                        isDisabled={!row.type} // disable until a type is selected
                                    />
                                </td>
                                <td className="text-center">
                                    <Button 
                                        color="danger" 
                                        size="sm" 
                                        onClick={() => handleRemoveAvailableLengthRow(idx)} 
                                    ><i className="fa fa-trash" /></Button>
                                    {isDuplicate && <div className="text-danger small mt-1">Duplicate row</div>}
                                </td>
                            </tr>
                        );
                    })}
                 </tbody>
             </table>
             <Button color="success" size="sm" className="mt-2" onClick={handleAddAvailableLengthRow}><i className="fa fa-plus" /> Add Row</Button>
         </FormGroup>
     );
 };

 export default AvailableIn;
